/**
 * Compound atom/bond ID utilities (PIPH-78 step 2).
 *
 * A "compound ID" addresses an atom or bond that lives inside a collapsed
 * group instance. It has the form `"<groupId>:<localId>"` — for example,
 * `"group_3:a_5"` refers to the atom with template-local ID `a_5` inside
 * the group instance with workspace ID `group_3`.
 *
 * Loose atoms and bonds (those that live directly in the workspace, not
 * inside a group instance) use plain stable IDs like `"a_42"` or `"b_17"`,
 * which by convention never contain a colon.
 *
 * These utilities are used by the workspace's transform-aware accessors
 * (added in PIPH-77) and by any consumer that needs to construct or parse
 * IDs that may reference group atoms.
 *
 * Today, no callers in the codebase generate compound IDs — these helpers
 * exist so that future code can use a single, consistent format. The format
 * is also locked in by the unit tests in this package, so consumers can
 * depend on it now without waiting for PIPH-77.
 */

/** Brand-style alias for clarity at API boundaries. */
export type GroupId = string;

/** Brand-style alias for clarity at API boundaries. */
export type CompoundId = string;

/** Separator between groupId and localId in a compound ID. */
const SEPARATOR = ':';

/**
 * Construct a compound ID from a group instance ID and a template-local ID.
 *
 * @example
 *   makeCompoundId('group_3', 'a_5') // → 'group_3:a_5'
 */
export function makeCompoundId(groupId: GroupId, localId: string): CompoundId {
  return `${groupId}${SEPARATOR}${localId}`;
}

/**
 * Returns true if the given ID references an atom or bond inside a group
 * instance — i.e., it has the `"<groupId>:<localId>"` form with non-empty
 * parts on both sides of the separator.
 *
 * Loose atom and bond IDs (which never contain a colon) return false.
 *
 * @example
 *   isCompoundId('a_42')         // → false
 *   isCompoundId('group_3:a_5')  // → true
 *   isCompoundId('group_3:')     // → false (empty localId)
 *   isCompoundId(':a_5')         // → false (empty groupId)
 */
export function isCompoundId(id: string): boolean {
  const sep = id.indexOf(SEPARATOR);
  return sep > 0 && sep < id.length - 1;
}

/**
 * Parse an ID into its `{ groupId, localId }` components.
 *
 * For loose IDs (no separator), returns `{ localId: id }` with no `groupId`.
 * For compound IDs, splits on the *first* separator so that local IDs may
 * themselves contain colons in the future without breaking the parser.
 *
 * Malformed compound IDs (empty groupId or empty localId) are treated as
 * loose IDs and returned as `{ localId: id }`.
 *
 * @example
 *   parseCompoundId('a_42')         // → { localId: 'a_42' }
 *   parseCompoundId('group_3:a_5')  // → { groupId: 'group_3', localId: 'a_5' }
 *   parseCompoundId('group_3:')     // → { localId: 'group_3:' }   (malformed)
 */
export function parseCompoundId(
  id: string,
): { groupId?: GroupId; localId: string } {
  if (!isCompoundId(id)) {
    return { localId: id };
  }
  const sep = id.indexOf(SEPARATOR);
  return {
    groupId: id.slice(0, sep),
    localId: id.slice(sep + 1),
  };
}
